/* Invitation tab — a participant invites a relative into their OWN dossier.
 *
 * Study-agnostic: it renders whatever invitation configs the researcher authored for the
 * signed-in participant's role (Outborn: a parent inviting the second parent). The server
 * pins every invitee to the sender's own numéro de dossier, so nothing here decides the
 * dossier binding — the page only chooses the role + channel and collects contact details.
 *
 * Reworked from the legacy "mentalo" invitation page: the self-recruitment gamification
 * (the "Goal" progress bar, the social-share menu) is gone; the reusable core stays — the
 * config pipeline (getInvitations → convertInvitations), the send form (InvitationFormModal)
 * and the public-link copy (getInvitPubLink).
 */
import { ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { IonIcon, useIonViewWillEnter } from "@ionic/react";
import {
  personAddOutline,
  mailOutline,
  chatbubbleEllipsesOutline,
  linkOutline,
} from "ionicons/icons";
import InvitationFormModal from "../../components/organisms/Invitation/InvitationFormModal";
import { convertInvitations } from "../../helper/invitation";
import {
  getInvitations,
  getInvitPubLink,
  sendInvitation,
} from "../../services/invitation.service";
import {
  InvitationChannelEnum,
  InvitationConfigType,
  InvitationDisplayType,
  sendInvitationFormType,
} from "../../types/invitation.types";
import { useConfig } from "../../contexts/ConfigContext";
import { useFetch } from "../../hooks/useFetch";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import Info from "../../components/atoms/ErrorComponent";
import useToast from "../../hooks/useToast";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";

/** Non-public channels open the form modal; a public link is copied to the clipboard. */
type ActiveSend = { channel: InvitationChannelEnum; configId: number };

const CHANNEL_ORDER: InvitationChannelEnum[] = [
  InvitationChannelEnum.EMAIL,
  InvitationChannelEnum.SMS,
  InvitationChannelEnum.PUBLICLINK,
];

const InvitationPage = (): ReactElement => {
  const { study } = useConfig();
  const present = useToast();
  const { setResonanceLayout } = useResonanceLayout();
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [active, setActive] = useState<ActiveSend | null>(null);

  useIonViewWillEnter(() => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      title: "invitation_tab_label",
      welcomePage: false,
    });
  });

  const {
    isError,
    isLoading,
    data: invitationConfigs,
  } = useFetch<InvitationConfigType[]>("invitations", getInvitations, {
    studyId: study.id,
    lang: language,
  });

  if (isLoading) return <ResonanceLoader />;

  if (isError)
    return (
      <Info
        title={t("common_server_error_title")}
        description={t("common_server_error_desc")}
        actionText={t("common_go_back")}
      />
    );

  // A role whose dossier already has a registered member is dropped BEFORE grouping —
  // convertInvitations does not carry the to_role_already_joined flag through.
  const sendable = (invitationConfigs ?? []).filter(
    (config) => !config.to_role_already_joined
  );
  const groups: InvitationDisplayType[] = convertInvitations(sendable);

  const copyToClipboard = (value: string): void => {
    const el = document.createElement("textarea");
    el.value = value;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
  };

  const handleChannel = async (
    channel: InvitationChannelEnum,
    configId: number
  ): Promise<void> => {
    if (channel === InvitationChannelEnum.PUBLICLINK) {
      const { data: link } = await getInvitPubLink(
        { studyId: study.id },
        { invitation_config_id: configId }
      );
      copyToClipboard(link as unknown as string);
      present(t("invite_copy_notification"));
      return;
    }
    // EMAIL / SMS → collect contact details in the shared form modal.
    setActive({ channel, configId });
  };

  const handleSend = async (values: sendInvitationFormType): Promise<void> => {
    if (!active) return;
    await sendInvitation(
      { study_id: study.id },
      {
        target_user: {
          firstname: values.firstname,
          lastname: values.lastname,
          email: values.email,
          phone_number: values.phone_number,
        },
        channel: active.channel,
        invitation_config_id: active.configId,
        lang: values.lang,
      }
    );
    present(t("invite_sent_toast"));
  };

  const channelMeta = (channel: InvitationChannelEnum) => {
    switch (channel) {
      case InvitationChannelEnum.EMAIL:
        return { icon: mailOutline, label: t("invite_action_email"), primary: true };
      case InvitationChannelEnum.SMS:
        return { icon: chatbubbleEllipsesOutline, label: t("invite_action_sms"), primary: false };
      case InvitationChannelEnum.PUBLICLINK:
      default:
        return { icon: linkOutline, label: t("invite_PUBLIC_LINK"), primary: false };
    }
  };

  return (
    <ResonanceLayout isConnected>
      <>
      <Page>
        <Hero>
          <HeroBadge>
            <IonIcon icon={personAddOutline} />
          </HeroBadge>
          <HeroTitle>{t("invite_page_title")}</HeroTitle>
          <HeroSubtitle>{t("invite_page_subtitle")}</HeroSubtitle>
        </Hero>

        {groups.length === 0 ? (
          <EmptyCard>{t("invite_empty_config")}</EmptyCard>
        ) : (
          <Cards>
            {groups.map((group) => {
              // Stable channel order (email first), de-duplicated per role.
              const channels = CHANNEL_ORDER.map((channel) =>
                group.channels.find((c) => c.channel === channel)
              ).filter((c): c is NonNullable<typeof c> => c != null);
              return (
                <RoleCard key={group.to_role_id}>
                  <RoleName>{group.to_role_label ?? group.to_role}</RoleName>
                  <RoleHint>{t("invite_role_hint")}</RoleHint>
                  <Actions>
                    {channels.map((c) => {
                      const meta = channelMeta(c.channel);
                      return (
                        <ActionButton
                          key={c.configId}
                          type="button"
                          $primary={meta.primary}
                          onClick={() => handleChannel(c.channel, c.configId)}
                        >
                          <IonIcon icon={meta.icon} />
                          <span>{meta.label}</span>
                        </ActionButton>
                      );
                    })}
                  </Actions>
                </RoleCard>
              );
            })}
          </Cards>
        )}
      </Page>

      {active && (
        <InvitationFormModal
          isOpen={active != null}
          onDismiss={() => setActive(null)}
          channel={active.channel}
          onSaveClick={handleSend}
        />
      )}
      </>
    </ResonanceLayout>
  );
};

export default InvitationPage;

const Page = styled.div`
  max-width: 640px;
  margin: 0 auto;
  padding: 8px 16px 40px;
`;

const Hero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px 8px 24px;
`;

const HeroBadge = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  background: rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.1);
  color: var(--app-color-primary, #2563eb);
  ion-icon {
    font-size: 30px;
  }
`;

const HeroTitle = styled.h1`
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  color: var(--app-color-text, #0f172a);
`;

const HeroSubtitle = styled.p`
  margin: 0;
  font-size: 15px;
  line-height: 1.45;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.62);
`;

const Cards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const RoleCard = styled.div`
  border-radius: 18px;
  padding: 18px 18px 16px;
  background: var(--app-color-surface, #fff);
  border: 1px solid rgba(var(--app-color-text-rgb, 15, 23, 42), 0.08);
  box-shadow: 0 6px 20px rgba(var(--app-color-text-rgb, 15, 23, 42), 0.06);
`;

const RoleName = styled.div`
  font-size: 17px;
  font-weight: 700;
  color: var(--app-color-text, #0f172a);
`;

const RoleHint = styled.div`
  margin: 4px 0 14px;
  font-size: 14px;
  line-height: 1.4;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.55);
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  /* Centred: the channel buttons are a variable-length set (a role may offer
     one channel or several), so a left-anchored row reads as ragged against
     the card. Centring keeps the group balanced at any count, and keeps the
     wrapped rows centred too rather than leaving a stranded last button. */
  justify-content: center;
  gap: 10px;
`;

const ActionButton = styled.button<{ $primary: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  border-radius: 999px;
  font-size: 14.5px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: opacity 0.15s ease, transform 0.05s ease;
  &:active {
    transform: scale(0.98);
  }
  ion-icon {
    font-size: 18px;
  }
  border: 1px solid
    ${({ $primary }) =>
      $primary ? "transparent" : "rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.35)"};
  background: ${({ $primary }) =>
    $primary
      ? "var(--app-color-primary, #2563eb)"
      : "rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.06)"};
  color: ${({ $primary }) =>
    $primary ? "#fff" : "var(--app-color-primary, #2563eb)"};
`;

const EmptyCard = styled.div`
  border-radius: 18px;
  padding: 28px 20px;
  text-align: center;
  font-size: 15px;
  color: rgba(var(--app-color-text-rgb, 15, 23, 42), 0.6);
  background: var(--app-color-surface, #fff);
  border: 1px dashed rgba(var(--app-color-text-rgb, 15, 23, 42), 0.18);
`;
